import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Tag, TagType } from "capitalrx-components-2.0/lib/general";

import {
  useIsParticipatingInMedicarePrescriptionPlan,
  useIsPendingParticipationInMedicarePrescriptionPaymentPlan,
  useIsParticipatingNextPlanYearInMedicarePrescriptionPlan,
  useMedicarePrescriptionPaymentPlanGracePeriod,
} from "@/App/hooks";

export const ParticipationStatus: FC<{ planYear: number }> = ({ planYear }) => {
  const { t: translate } = useTranslation();
  const { isParticipating } = useIsParticipatingInMedicarePrescriptionPlan(planYear);
  const { isPendingParticipation } = useIsPendingParticipationInMedicarePrescriptionPaymentPlan(planYear);
  const { isParticipatingNextPlanYear } = useIsParticipatingNextPlanYearInMedicarePrescriptionPlan(planYear);
  const { isInGracePeriod } = useMedicarePrescriptionPaymentPlanGracePeriod(planYear);

  const tagStyle: TagType = useMemo(() => {
    if (isParticipating || isParticipatingNextPlanYear) {
      if (isInGracePeriod) {
        return "error-high-contrast";
      } else {
        return "success-high-contrast";
      }
    } else if (isPendingParticipation) {
      return "warning-high-contrast";
    } else {
      return "info-high-contrast";
    }
  }, [isParticipating, isPendingParticipation, isInGracePeriod, isParticipatingNextPlanYear]);

  const status = useMemo(() => {
    if (isParticipating) {
      if (isInGracePeriod) {
        return `${translate("PARTICIPATION_STATUS.participating")} - ${translate("PARTICIPATION_STATUS.paymentPastDue")}`;
      } else if (isParticipatingNextPlanYear) {
        return translate("PARTICIPATION_STATUS.participatingNextPlanYear");
      } else {
        return translate("PARTICIPATION_STATUS.participating");
      }
    } else if (isPendingParticipation) {
      return translate("PARTICIPATION_STATUS.pending");
    } else {
      return translate("PARTICIPATION_STATUS.notParticipating");
    }
  }, [isParticipating, isPendingParticipation, isInGracePeriod, isParticipatingNextPlanYear]);

  return (
    <Tag type={tagStyle}>{status}</Tag>
  );
};
